/* global runtime */
// Kontoplan, saldobalanse, MVA-oppgavemodell (deriveVat/buildVatFilingModel) og anleggsmidler.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.

const accountNumber = (code) => {
  const match = String(code || '').trim().match(/^(\d{4,5})/);
  return match ? Number(match[1]) : Number(String(code || '').trim());
};
const isIncomeAccountCode = (code) => {
  const value = accountNumber(code);
  return Number.isFinite(value) && value >= 3000 && value <= 3999;
};
const isCostAccountCode = (code) => {
  const value = accountNumber(code);
  return Number.isFinite(value) && value >= 4000 && value <= 8999;
};
const isFinanceAccountCode = (code) => {
  const value = accountNumber(code);
  return Number.isFinite(value) && value >= 8000 && value <= 8999;
};
const isClosingOrTaxDispositionAccountCode = (code) => {
  const value = accountNumber(code);
  return Number.isFinite(value) && ((value >= 8300 && value <= 8399) || (value >= 8900 && value <= 8999));
};
const isImportedClosingTransferAccountCode = (code) => /^89\d{2}$/.test(String(code || '').trim());
// Maneden en post horer til, eller -1 om den faller utenfor perioden. Manedsgrafen
// trenger bade filteret og maneden, og far begge av ett oppslag i stedet for a kalle
// filteret tolv ganger per post.
const reportMonthIndex = (value, year, options = {}) => {
  const iso = nbToIso(value);
  const fiscal = options.fiscalBounds;
  if (fiscal ? iso < fiscal.from || iso > fiscal.to : Number(iso.slice(0, 4)) !== year) return -1;
  if (options.from && iso < options.from) return -1;
  if (options.to && iso > options.to) return -1;
  const monthIndex = fiscal ? (Number(iso.slice(0, 4)) - Number(fiscal.from.slice(0, 4))) * 12 + Number(iso.slice(5, 7)) - Number(fiscal.from.slice(5, 7)) : Number(iso.slice(5, 7)) - 1;
  const months = Array.isArray(options.months) ? options.months : [0, fiscal ? 17 : 11];
  return monthIndex >= months[0] && monthIndex <= months[1] ? monthIndex : -1;
};
const reportDateInRange = (value, year, options = {}) => reportMonthIndex(value, year, options) >= 0;
// Den norske standardkontoplanen ligger i chart-accounts-no.jsx og lastes som
// klassisk script før denne fila. Den er ren data og vokser med kontoplanen;
// motoren her eier bare hvordan frøene slås sammen med foretakets egne kontoer.
const SWEDEN_CHART_ACCOUNT_SEEDS = [
  ['1010', 'Balanserade utgifter'],
  ['1220', 'Inventarier och verktyg'],
  ['1229', 'Ackumulerade avskrivningar på inventarier'],
  ['1250', 'Datorer'],
  ['1259', 'Ackumulerade avskrivningar på datorer'],
  ['1350', 'Andelar och värdepapper'],
  ['1400', 'Varulager'],
  ['1510', 'Kundfordringar'],
  ['1580', 'Övriga kortfristiga fordringar'],
  ['1700', 'Förutbetalda kostnader'],
  ['1910', 'Kassa'],
  ['1930', 'Företagskonto'],
  ['2010', 'Eget kapital'],
  ['2013', 'Egna uttag'],
  ['2018', 'Egna insättningar'],
  ['2019', 'Årets resultat'],
  ['2350', 'Långfristiga skulder till kreditinstitut'],
  ['2440', 'Leverantörsskulder'],
  ['2610', 'Utgående moms'],
  ['2611', 'Utgående moms 25 %'],
  ['2621', 'Utgående moms 12 %'],
  ['2631', 'Utgående moms 6 %'],
  ['2640', 'Ingående moms'],
  ['2641', 'Debiterad ingående moms'],
  ['2650', 'Redovisningskonto för moms'],
  ['3001', 'Försäljning inom Sverige', 'income'],
  ['3041', 'Försäljning tjänster 25 % moms', 'income'],
  ['3042', 'Försäljning tjänster 12 % moms', 'income'],
  ['3043', 'Försäljning tjänster 6 % moms', 'income'],
  ['3051', 'Försäljning varor 25 % moms', 'income'],
  ['3052', 'Försäljning varor 12 % moms', 'income'],
  ['3053', 'Försäljning varor 6 % moms', 'income'],
  ['3100', 'Försäljning utanför Sverige', 'income'],
  ['3106', 'Försäljning varor till annat EU-land', 'income'],
  ['3200', 'Försäljning utan moms', 'income'],
  ['3308', 'Försäljning tjänster till annat EU-land', 'income'],
  ['3900', 'Övriga rörelseintäkter', 'income'],
  ['4010', 'Varuinköp'],
  ['4515', 'Inköp varor från annat EU-land'],
  ['4535', 'Inköp tjänster från annat EU-land'],
  ['4545', 'Import av varor'],
  ['5010', 'Lokalhyra'],
  ['5020', 'El för belysning'],
  ['5220', 'Hyra av inventarier och verktyg'],
  ['5410', 'Förbrukningsinventarier'],
  ['5460', 'Förbrukningsmaterial'],
  ['5611', 'Drivmedel för personbilar'],
  ['5800', 'Resekostnader'],
  ['5810', 'Biljetter'],
  ['5930', 'Reklamtrycksaker och direktreklam'],
  ['6071', 'Representation, avdragsgill'],
  ['6110', 'Kontorsmaterial'],
  ['6212', 'Mobiltelefon'],
  ['6250', 'Postbefordran'],
  ['6310', 'Företagsförsäkringar'],
  ['6351', 'Konstaterade kundförluster'],
  ['6420', 'Ersättning till revisor'],
  ['6530', 'Redovisningstjänster'],
  ['6540', 'IT-tjänster'],
  ['6570', 'Bankkostnader'],
  ['6970', 'Tidningar och facklitteratur'],
  ['6990', 'Övriga externa kostnader'],
  ['7832', 'Avskrivningar på inventarier och verktyg'],
  ['8310', 'Ränteintäkter', 'income'],
  ['8420', 'Räntekostnader'],
];
const SWEDEN_LIMITED_CHART_ACCOUNT_SEEDS = [
  ['2081', 'Aktiekapital'],
  ['2091', 'Balanserad vinst eller förlust'],
  ['2099', 'Årets resultat'],
  ['2510', 'Skatteskulder'],
  ['2710', 'Personalskatt'],
  ['2731', 'Avräkning sociala avgifter'],
  ['2893', 'Skulder till närstående personer'],
  ['2898', 'Outtagen vinstutdelning'],
  ['2940', 'Upplupna löner'],
  ['2990', 'Övriga upplupna kostnader'],
  ['7010', 'Löner till tjänstemän'],
  ['7510', 'Arbetsgivaravgifter'],
  ['8910', 'Skatt på årets resultat'],
];
const DENMARK_CHART_ACCOUNT_SEEDS = [
  ['1200', 'Inventar'],
  ['1209', 'Akkumulerede afskrivninger på inventar'],
  ['1250', 'IT-udstyr'],
  ['1259', 'Akkumulerede afskrivninger på IT-udstyr'],
  ['1300', 'Debitorer'],
  ['1400', 'Varelager'],
  ['1500', 'Andre tilgodehavender'],
  ['1700', 'Forudbetalte omkostninger'],
  ['1900', 'Kasse'],
  ['1920', 'Bank'],
  ['2000', 'Egenkapital', 'equity-liability'],
  ['2040', 'Privat hævning', 'equity-liability'],
  ['2050', 'Private indskud', 'equity-liability'],
  ['2055', 'Årets resultat', 'equity-liability'],
  ['2200', 'Langfristet gæld til kreditinstitutter', 'equity-liability'],
  ['2250', 'Mellemregning med ejere og ledelse', 'equity-liability'],
  ['2400', 'Kreditorer', 'equity-liability'],
  ['2510', 'Skyldig skat', 'equity-liability'],
  ['6800', 'Salgsmoms', 'equity-liability'],
  ['6810', 'Købsmoms', 'equity-liability'],
  ['6900', 'Momsafregning', 'equity-liability'],
  ['1000', 'Omsætning', 'income'],
  ['1010', 'Omsætning uden moms', 'income'],
  ['1020', 'Ydelsessalg', 'income'],
  ['1030', 'Varesalg', 'income'],
  ['1040', 'EU-varesalg uden moms', 'income'],
  ['1050', 'EU-ydelsessalg uden moms', 'income'],
  ['1060', 'Eksport og andet momsfrit salg', 'income'],
  ['1090', 'Øvrige driftsindtægter', 'income'],
  ['4000', 'Varekøb', 'cost'],
  ['4100', 'Fremmed arbejde', 'cost'],
  ['4200', 'Varekøb fra EU', 'cost'],
  ['4300', 'Ydelseskøb fra udlandet', 'cost'],
  ['4400', 'Import af varer', 'cost'],
  ['5010', 'Husleje', 'cost'],
  ['5020', 'El og varme', 'cost'],
  ['5200', 'Leje af udstyr', 'cost'],
  ['5400', 'Småanskaffelser', 'cost'],
  ['5500', 'Rejseomkostninger', 'cost'],
  ['5600', 'Kontorartikler', 'cost'],
  ['5610', 'Telefon og internet', 'cost'],
  ['5700', 'Annoncer og reklame', 'cost'],
  ['5800', 'Software og abonnementer', 'cost'],
  ['5850', 'Revisor og regnskabsassistance', 'cost'],
  ['5900', 'Øvrige omkostninger', 'cost'],
  ['6500', 'Bankgebyrer', 'cost'],
  ['6600', 'Afskrivninger på inventar og IT-udstyr', 'cost'],
  ['7500', 'Renteindtægter', 'income'],
  ['7600', 'Renteomkostninger', 'cost'],
];
const DENMARK_LIMITED_CHART_ACCOUNT_SEEDS = [
  ['2100', 'Selskabskapital', 'equity-liability'],
  ['2110', 'Overført resultat', 'equity-liability'],
  ['2120', 'Årets resultat', 'equity-liability'],
  ['2500', 'Skyldig selskabsskat', 'equity-liability'],
  ['2600', 'A-skat', 'equity-liability'],
  ['2700', 'AM-bidrag', 'equity-liability'],
  ['2900', 'Skyldig løn', 'equity-liability'],
  ['2950', 'Skyldige omkostninger', 'equity-liability'],
  ['7000', 'Lønninger', 'cost'],
  ['7100', 'Arbejdsgiverafgifter', 'cost'],
  ['8200', 'Skat af årets resultat', 'cost'],
];
const chartAccountCategory = (code) => {
  const text = String(code || '').trim();
  if (/^3\d{3,4}$/.test(text)) return 'income';
  const value = accountNumber(code);
  if (Number.isFinite(value)) {
    if (value >= 1000 && value <= 1999) return 'asset';
    if (value >= 2000 && value <= 2999) return 'equity-liability';
    if (value >= 3000 && value <= 3999) return 'income';
  }
  return 'cost';
};
const normalizeChartCountry = (settings) => {
  const country = String(typeof settings === 'string' ? settings : settings?.country || 'NO').toUpperCase();
  return ['NO', 'SE', 'DK'].includes(country) ? country : 'NO';
};
const normalizeCompanyTypeCode = (settings) =>
  String(settings?.companyTypeCode || settings?.companyTypeLabel || '').trim().toUpperCase().split(/[\s.-]+/)[0] || '';
const isLimitedLiabilitySettings = (settings) => ['AS', 'ASA', 'NUF', 'AB', 'APS', 'A/S'].includes(normalizeCompanyTypeCode(settings));
const chartAccountSeedsForSettings = (settings) => {
  const country = normalizeChartCountry(settings);
  if (country === 'SE') {
    return [
      ...SWEDEN_CHART_ACCOUNT_SEEDS,
      ...(isLimitedLiabilitySettings(settings) ? SWEDEN_LIMITED_CHART_ACCOUNT_SEEDS : []),
    ];
  }
  if (country === 'DK') {
    return [
      ...DENMARK_CHART_ACCOUNT_SEEDS,
      ...(isLimitedLiabilitySettings(settings) ? DENMARK_LIMITED_CHART_ACCOUNT_SEEDS : []),
    ];
  }
  return PLUMME1_NO_CHART_ACCOUNT_SEEDS;
};
// Fjerde element i et frø slår kontoen av. Den blir da borte fra kontovelgerne,
// men beholder navnet sitt: saldobalansen lager raden av posteringen og slår opp
// navnet her, så en gammel føring på en avslått konto vises fortsatt riktig.
// Brukes på kontoer som er blitt liggende ved siden av en annen konto med samme
// betydning — se kommentarene i chart-accounts-no.jsx.
// The seeds are static per country and company form, so the built chart is too. Without the
// cache every posting line rebuilt and sorted the whole chart to look up one name
// (plumme1AccountName), which dominated report time on large journals (speed track S0).
const standardChartCache = new Map();
const standardChartKey = (settings) => `${normalizeChartCountry(settings)}:${isLimitedLiabilitySettings(settings) ? 'limited' : 'other'}`;
const buildStandardChartAccounts = (settings) => {
  const key = standardChartKey(settings);
  const cached = standardChartCache.get(key);
  if (cached) return cached.accounts;
  const accounts = new Map();
  chartAccountSeedsForSettings(settings).forEach(([code, name, category, active]) => {
    accounts.set(code, { id: code, code, name, category: category || chartAccountCategory(code), active: active !== false, locked: true });
  });
  const sorted = [...accounts.values()].sort((a, b) => a.code.localeCompare(b.code, 'nb-NO'));
  standardChartCache.set(key, { accounts: sorted, byCode: accounts });
  return sorted;
};
const standardChartAccountByCode = (settings, code) => {
  buildStandardChartAccounts(settings);
  return standardChartCache.get(standardChartKey(settings)).byCode.get(String(code || '').trim()) || null;
};
const plumme1StandardChartAccounts = buildStandardChartAccounts('NO');
const mergePlumme1ChartAccounts = (customAccounts = [], settings) => {
  const accounts = new Map(buildStandardChartAccounts(settings).map((account) => [account.code, { ...account }]));
  safeArray(customAccounts).forEach((account) => {
    const code = String(account.code || account.accountNumber || '').trim();
    if (!code) return;
    const canonicalAccount = accounts.get(code);
    accounts.set(code, {
      ...account,
      id: account.id || code,
      code,
      name: String(account.name || '').trim() || `Konto ${code}`,
      category: account.category || chartAccountCategory(code),
      active: account.active !== false,
      locked: account.locked ?? false,
      yearClosingStandardAccountCode: account.yearClosingStandardAccountCode || account.equivalentCode || account.standardAccountCode || account.mapsToAccountCode || canonicalAccount?.code || '',
    });
  });
  return [...accounts.values()].sort((a, b) => a.code.localeCompare(b.code, 'nb-NO'));
};
const plumme1AccountName = (code, fallback, settings) =>
  standardChartAccountByCode(settings, code)?.name || fallback || `Konto ${code}`;
const reportAccountName = plumme1AccountName;
const reportAccountCategory = (state, code) => trialAccount(state, code)?.category || chartAccountCategory(code);
const isReportIncomeAccountCode = (state, code) =>
  Boolean(String(code || '').trim()) && (reportAccountCategory(state, code) === 'income' || isIncomeAccountCode(code));
const isReportCostAccountCode = (state, code) =>
  Boolean(String(code || '').trim()) && !isClosingOrTaxDispositionAccountCode(code) && (reportAccountCategory(state, code) === 'cost' || isCostAccountCode(code));
const reportLineNet = (line) => {
  const base = rentalLineBase(line);
  const discount = num(line.discountValue, 0);
  if (!discount) return base;
  const discountBasis = Math.abs(base);
  const discountAmount = line.discountType === 'amount'
    ? Math.min(discountBasis, discount)
    : Math.min(discountBasis, discountBasis * (Math.min(100, discount) / 100));
  return base - (base < 0 ? -discountAmount : discountAmount);
};
const reportSalesAccountCode = (state, entry) => {
  const code = String(entry?.salesAccountCode || '').trim();
  if (isReportIncomeAccountCode(state, code) && code !== '3000') return code;
  const treatment = entry?.vatTreatment || (num(entry?.vatRate) === 0 ? 'zeroRated' : 'standard');
  if (num(entry?.vatRate) === 0 || treatment === 'zeroRated' || treatment === 'exempt') return '3100';
  return isReportIncomeAccountCode(state, code) ? code : '3000';
};
const pushReportAmount = (map, state, code, name, amount) => {
  const cleanCode = String(code || '').trim();
  const value = num(amount);
  if (!cleanCode || !value) return;
  const current = map.get(cleanCode) || { nr: cleanCode, name: reportAccountName(cleanCode, name, state?.settings), amount: 0 };
  current.amount += value;
  map.set(cleanCode, current);
};
const reportLinesFromMap = (map, total, subtract = new Map()) =>
  [...new Set([...map.keys(), ...subtract.keys()])].map(code => ({
    ...(map.get(code) || subtract.get(code)), amount: num(map.get(code)?.amount) - num(subtract.get(code)?.amount),
  }))
    .sort((a, b) => a.nr.localeCompare(b.nr, 'nb-NO'))
    .map((line) => ({
      ...line,
      amount: Math.round(line.amount * 100) / 100,
      pct: total ? Math.round((Math.abs(line.amount) / Math.abs(total)) * 100) : 0,
    }));

const TRIAL_GROUPS = [
  { key: 'eiendeler', labelKey: 'rep.group.assets', match: (account) => account.category ? account.category === 'asset' : accountNumber(account.nr) >= 1000 && accountNumber(account.nr) <= 1999 },
  { key: 'egenkapital', labelKey: 'rep.group.equityLiabilities', match: (account) => account.category ? account.category === 'equity-liability' : accountNumber(account.nr) >= 2000 && accountNumber(account.nr) <= 2999 },
  { key: 'driftsinntekter', labelKey: 'rep.group.operatingIncome', match: (account) => account.category ? account.category === 'income' : accountNumber(account.nr) >= 3000 && accountNumber(account.nr) <= 3999 },
  { key: 'driftskostnader', labelKey: 'rep.group.operatingCosts', match: (account) => account.category ? account.category === 'cost' : accountNumber(account.nr) >= 4000 && accountNumber(account.nr) <= 8999 },
];
const reportTrialAccountContexts = new WeakMap();
const trialAccount = (state, code) => {
  const cleanCode = String(code || '').trim();
  return (reportTrialAccountContexts.get(state) || mergePlumme1ChartAccounts(state.chartAccounts, state.settings)).find((account) => account.code === cleanCode || account.accountNumber === cleanCode);
};
const trialBankAccountNumber = (state, code) => {
  const cleanCode = String(code || '').trim();
  if (!cleanCode) return '';
  const account = safeArray(state.settings?.bankAccounts).find((entry) =>
    String(entry.ledgerAccountCode || entry.ledger || entry.accountCode || '').trim() === cleanCode ||
    String(entry.code || '').trim() === cleanCode
  );
  if (account?.accountNumber) return String(account.accountNumber).trim();
  const fallbackLedger = String(state.settings?.bankAccountingAccount || '1920').trim();
  return cleanCode === fallbackLedger && state.settings?.bankAccount ? String(state.settings.bankAccount).trim() : '';
};
const trialAccountDisplayName = (state, code, name) => {
  const baseName = String(name || '').trim();
  const bankNumber = trialBankAccountNumber(state, code);
  if (!bankNumber || baseName.includes(bankNumber)) return baseName;
  return `${baseName} (${bankNumber})`;
};
const trialAccountName = (state, code, fallback) => {
  const cleanCode = String(code || '').trim();
  const chartAccount = trialAccount(state, cleanCode);
  return trialAccountDisplayName(state, cleanCode, chartAccount?.name || reportAccountName(cleanCode, fallback));
};
const trialAccountCategory = (state, code) => trialAccount(state, code)?.category || chartAccountCategory(code);
const addTrialAmount = (map, state, code, amount, name) => {
  const cleanCode = String(code || '').trim();
  const value = num(amount);
  if (!cleanCode || !value) return;
  const current = map.get(cleanCode) || { nr: cleanCode, name: trialAccountName(state, cleanCode, name), category: trialAccountCategory(state, cleanCode), ut: 0 };
  current.ut += value;
  map.set(cleanCode, current);
};
const addPostingToTrial = (map, state, postingLine) => {
  addTrialAmount(map, state, postingLine.nr || postingLine.accountCode, postingLine.amount ?? postingLine.signedAmount, postingLine.name || postingLine.description);
};
const importedReportStatsForYear = (state, year) => state.accountingImportReportStatsByYear?.[String(year)] || null;
const usesImportedReportingBasis = (state, year) => state.accountingBasisCoverage?.reporting_basis?.kind === 'imported_accounting_statistics'
  && state.accountingBasisCoverage.reporting_basis.validated === true
  && Number(state.accountingBasisCoverage.reporting_basis.year) === Number(year);
const importedReportMonthInRange = (month, year, options = {}) => {
  const monthNumber = Number(month);
  if (!Number.isInteger(monthNumber) || monthNumber < 1 || monthNumber > 12) return false;
  if (options.from || options.to) {
    const date = monthEndIso(Number(year), monthNumber - 1);
    return reportDateInRange(date, year, options);
  }
  const months = Array.isArray(options.months) ? options.months : [0, 11];
  return monthNumber - 1 >= num(months[0], 0) && monthNumber - 1 <= num(months[1], 11);
};
const addImportedOpeningBalanceToTrial = (map, state, year) => {
  safeArray(importedReportStatsForYear(state, year)?.accounts).forEach((account) => {
    addTrialAmount(map, state, account.accountCode, num(account.opening), account.name);
  });
};
const addImportedReportMovementsToTrial = (map, state, year, options = {}) => {
  safeArray(importedReportStatsForYear(state, year)?.monthly).forEach((row) => {
    if (!importedReportMonthInRange(row.month, year, options)) return;
    addTrialAmount(map, state, row.accountCode, num(row.movement), row.name);
  });
};
const expenseSettlementAccount = (expense) => {
  if (expense.settlementAccountCode) return expense.settlementAccountCode;
  if (expense.paidBy === 'private') return '2060';
  if (expense.paidBy === 'cash') return '1900';
  if (expense.paidBy === 'loan') return '2250';
  return '1920';
};

const trialExpensePostings = (expense) => {
  if (expense.manualPosting && safeArray(expense.manualPostingLines).length) return expensePostings(expense);
  const lines = safeArray(expense.purchaseLines).length ? safeArray(expense.purchaseLines) : [expense];
  const costPostings = [];
  const inputVatPostings = [];
  const outputVatPostings = [];
  lines.forEach((line) => {
    const beforeCounts = [costPostings.length, inputVatPostings.length, outputVatPostings.length];
    const amountNet = Math.max(0, num(line.amountNet, num(expense.amountNet)));
    const vatTreatment = line.vatTreatment || expense.vatTreatment || (num(line.vatRate, expense.vatRate) === 0 ? 'zeroRated' : 'standard');
    const vatRate = line.vatRate ?? expense.vatRate;
    const partialExpense = { ...expense, ...line, amountNet, vatRate, vatTreatment, reverseChargeCode: line.reverseChargeCode || '', purchaseLines: [], roundingAmount: 0 };
    const breakdown = dashboardExpenseVatBreakdown(partialExpense);
    const costCode = line.costAccountCode || expense.fixedAsset?.assetAccountCode || (expense.fixedAsset ? window.PlummeFixedAssetTax.group(expense.fixedAsset.assetGroup || 'd')?.assetAccountCode : '') || expense.costAccountCode || expense.accountCode || expense.expenseAccountCode || expense.categoryAccountCode || '6300';
    if (Math.abs(breakdown.costAmount) > 0.005) costPostings.push(posting(costCode, line.description || expense.category || expense.description || 'Kostnad', expense.description || 'Kj\u00f8p', breakdown.costAmount));
    if (breakdown.deductibleVat) inputVatPostings.push(posting(norwegianInputVatAccount(vatRate), 'Inng\u00e5ende mva', expense.description || 'Kj\u00f8p', breakdown.deductibleVat,
      String(line.purchaseVatCode || expense.purchaseVatCode || '') === '14'
        ? { saftTaxInformation: { code: '14', rate: 25, base: 0, amount: breakdown.deductibleVat, side: 'debit' } }
        : {}));
    if (breakdown.reverseChargeOutputVat) outputVatPostings.push(posting(norwegianOutputVatAccount(vatRate), 'Utg\u00e5ende mva snudd avgift', expense.description || 'Kj\u00f8p', -breakdown.reverseChargeOutputVat));
    [costPostings, inputVatPostings, outputVatPostings].forEach((rows, index) => rows.slice(beforeCounts[index]).forEach((entry) => { entry.reportDimensions = { projectId: line.projectId || expense.projectId || '', departmentId: line.departmentId || expense.departmentId || '' }; }));
  });
  const totals = dashboardExpenseVatBreakdown(expense);
  const adjustLast = (rows, expected) => {
    if (!rows.length) return;
    const current = rows.reduce((sum, line) => sum + num(line.amount), 0);
    rows[rows.length - 1].amount = voucherMoney(num(rows[rows.length - 1].amount) + voucherMoney(expected - current));
  };
  const invoiceRounding = voucherMoney(expense.roundingAmount);
  adjustLast(costPostings, voucherMoney(totals.costAmount - invoiceRounding));
  adjustLast(inputVatPostings, voucherMoney(totals.deductibleVat));
  adjustLast(outputVatPostings, -voucherMoney(totals.reverseChargeOutputVat));
  const roundingPostings = invoiceRounding
    ? [posting(expense.roundingAccountCode || '7740', 'Øreavrunding', expense.description || 'Kjøp', invoiceRounding)]
    : [];
  const postings = [...costPostings, ...inputVatPostings, ...outputVatPostings, ...roundingPostings];
  const gross = Math.round(postings.reduce((sum, line) => sum + num(line.amount), 0) * 100) / 100;
  postings.push(posting('2400', 'Leverand\u00f8rgjeld', expense.supplierName || expense.description || 'Kj\u00f8p', -gross));
  return postings.filter(Boolean);
};
const directPurchasePaymentEvent = (expense) => {
  if (!isActiveExpense(expense) || expense.manualPosting || expense.isSupplierInvoice) return null;
  const amount = -num(trialExpensePostings(expense).find((line) => line.nr === '2400')?.amount);
  if (amount <= 0.005) return null;
  const settlementAccountCode = expenseSettlementAccount(expense);
  const attachments = voucherAttachments(expense);
  return {
    id: `${expense.id}-payment`,
    documentType: 'supplier_invoice',
    documentId: expense.id,
    direction: 'outgoing',
    amount,
    date: expense.paidAt || expense.payDate || expense.date,
    settlementAccountCode,
    voucherNumber: `${expense.voucherNumber || expense.id}-BET`,
    reference: expense.description || expense.supplierName || 'Betaling av kjøp',
    status: 'active',
    hasAttachment: attachments.length > 0,
    attachments,
  };
};
const historyVoucherPair = ({ id, sourceType, sourceId, kind, number, name, date, reversedAt, attachments = [], postings }) => {
  const original = {
    id: `${id}-original`, sourceType, sourceId, kind, num: number || id, name,
    date: isoToNb(date), status: 'reversed', hasAttachment: attachments.length > 0,
    attachments, postings,
  };
  const reversal = {
    id: `${id}-reversal`, sourceType: `${sourceType}Reversal`, sourceId, kind: 'reversal',
    num: `${number || id}-R`, name: `Reversering ${name}`, date: isoToNb(reversedAt || date),
    status: 'active', reversesEntryId: original.id, hasAttachment: false,
    postings: safeArray(postings).map((line) => ({ ...line, ...posting(line.nr, line.name, `Reversal ${line.desc || name}`, -num(line.amount)) })),
  };
  return [original, reversal];
};
const annulledPurchaseAccountingHistory = (state) => safeArray(state.expenses)
  .filter((expense) => expense?.status === 'annulled')
  .flatMap((expense) => {
    const documentPostings = trialExpensePostings({ ...expense, status: 'active' });
    const payments = expense.isSupplierInvoice
      ? safeArray(state.payments)
          .filter((payment) => payment.documentType === 'supplier_invoice' && payment.documentId === expense.id)
      : [directPurchasePaymentEvent({ ...expense, status: 'active' })].filter(Boolean);
    const documentHistory = historyVoucherPair({
      id: `${expense.id}-document`,
      sourceType: 'expenseHistory',
      sourceId: expense.id,
      kind: expense.isSupplierInvoice ? 'leverandorfaktura' : 'kjop',
      number: expense.supplierInvoiceNumber || expense.voucherNumber || expense.id,
      name: expense.supplierName || expense.description || 'Kj\u00f8p',
      date: expense.date,
      reversedAt: globalThis.PlummeAccountingScope.accountingEventDate(state, expense.reversalDate || expense.annulledAt || expense.updatedAt || expense.date),
      attachments: voucherAttachments(expense),
      postings: documentPostings,
    });
    const paymentHistory = payments.flatMap((payment, index) => historyVoucherPair({
      id: payment.id || `${expense.id}-payment-${index + 1}`,
      sourceType: 'expensePaymentHistory',
      sourceId: expense.id,
      kind: 'betaling',
      number: payment.reference || `${expense.voucherNumber || expense.id}-BET`,
      name: payment.reference || `Betaling ${expense.supplierName || expense.description || 'kj\u00f8p'}`,
      date: payment.date || expense.paidAt || expense.date,
      reversedAt: globalThis.PlummeAccountingScope.accountingEventDate(state, payment.reversedAt || expense.reversalDate || expense.annulledAt || expense.updatedAt || payment.date || expense.date),
      postings: trialPaymentPostings({ ...payment, status: 'active' }),
    }));
    return [...documentHistory, ...paymentHistory];
  });
const annulledCustomsAccountingHistory = (state) => safeArray(state.customsDeclarations)
  .filter((entry) => entry?.status === 'annulled')
  .flatMap((entry) => historyVoucherPair({
    id: entry.id,
    sourceType: 'customsHistory',
    sourceId: entry.id,
    kind: 'tolldeklarasjon',
    number: entry.declarationReference || entry.id,
    name: entry.description || 'Tolldeklarasjon',
    date: entry.date,
    reversedAt: globalThis.PlummeAccountingScope.accountingEventDate(state, entry.annulledAt || entry.updatedAt || entry.date),
    attachments: voucherAttachments(entry),
    postings: customsDeclarationPostings({ ...entry, status: 'active' }),
  }));
const trialPaymentPostings = (payment) => {
  if (payment.status === 'reversed' || payment.settlementKind === 'no_payment') return [];
  const amount = num(payment.carryingAmount ?? payment.amount); if (!amount) return [];
  const outgoing = payment.direction === 'outgoing'; if (!outgoing && payment.direction !== 'incoming') return [];
  const settlement = payment.settlementAccountCode || '1920'; const counterCode = payment.documentType === 'supplier_invoice' ? '2400' : '1500';
  const label = payment.reference || (outgoing ? 'Betaling' : 'Innbetaling'); const feeAmount = Math.max(0, num(payment.feeAmount));
  const bankAmount = payment.bankAmount == null || payment.bankAmount === '' ? (outgoing ? amount + feeAmount : amount - feeAmount) : num(payment.bankAmount);
  const feeCode = payment.feeAccountCode || '7770'; const fee = feeAmount > 0 ? posting(feeCode, plumme1AccountName(feeCode, 'Bank- og kortgebyr'), label, feeAmount) : null;
  const bank = posting(settlement, plumme1AccountName(settlement, 'Oppgj\u00f8rskonto'), label, outgoing ? -bankAmount : bankAmount);
  const counter = posting(counterCode, counterCode === '2400' ? 'Leverand\u00f8rgjeld' : 'Kundefordringer', label, outgoing ? amount : -amount);
  const difference = voucherMoney(-(bank.amount + counter.amount + num(fee?.amount))); const differenceCode = payment.exchangeDifferenceAccountCode || (difference < 0 ? '8060' : '8160');
  const exchange = Math.abs(difference) > 0.005 ? posting(differenceCode, plumme1AccountName(differenceCode, difference < 0 ? 'Valutagevinst' : 'Valutatap'), label, difference) : null;
  return outgoing ? [counter, fee, exchange, bank].filter(Boolean) : [bank, fee, counter, exchange].filter(Boolean);
};
const reportDimensionApi = () => window.PlummeReportDimensions || { active: () => false, includeUnassignedBase: () => true, matchesVoucher: () => true, postings: (_state, voucher) => safeArray(voucher.postings), options: () => ({ projects: [], departments: [], unassignedValue: '__unassigned__' }) };
const payrollRunPostingDate = (run) => {
  const period = String(run?.period || '').match(/^(20\d{2})-(0[1-9]|1[0-2])/);
  if (!period) return nbToIso(run?.paymentDate || run?.date || run?.approvedAt || '');
  const year = Number(period[1]);
  const month = Number(period[2]);
  return `${year}-${period[2]}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}`;
};
const payrollRunVoucherNumber = (run, allRuns = []) => {
  const raw = String(run?.voucherNumber || run?.runNumber || '').trim();
  if (raw) return raw;
  const period = String(run?.period || payrollRunPostingDate(run)?.slice(0, 7) || '').slice(0, 7);
  const candidates = safeArray(allRuns).filter((entry) =>
    ['approved', 'paid', 'reversed'].includes(entry?.status) &&
    String(entry?.period || '').slice(0, 7) === period
  );
  const ordered = candidates.length ? candidates : [run];
  const index = Math.max(0, ordered.findIndex((entry) => String(entry?.id) === String(run?.id)));
  return `LØNN-${period || 'UKJENT'}-${index + 1}`;
};
const payrollTaxPaymentTerm = (payment = {}) => {
  const explicit = Number(payment.term);
  if (Number.isInteger(explicit) && explicit >= 1 && explicit <= 6) return explicit;
  const month = Number(String(payment.date || '').slice(5, 7));
  return Number.isInteger(month) && month >= 1 && month <= 12 ? Math.ceil(month / 2) : 0;
};
const payrollTaxPaymentVoucherNumber = (payment = {}, entries = []) => {
  const raw = String(payment.voucherNumber || payment.paymentNumber || '').trim();
  if (raw) return raw;
  const year = String(payment.year || String(payment.date || '').slice(0, 4) || 'UKJENT');
  const term = payrollTaxPaymentTerm(payment);
  if (payment.kind === 'withholding') {
    const sameRun = safeArray(entries).filter((entry) =>
      entry?.status !== 'reversed' && entry?.kind === 'withholding' &&
      String(entry?.runId || '') === String(payment?.runId || '') &&
      String(entry?.id) !== String(payment?.id)
    );
    return `TREKK-${String(payment.date || '').slice(0, 10) || year}-${sameRun.length + 1}`;
  }
  const sameTerm = safeArray(entries).filter((entry) =>
    entry?.status !== 'reversed' &&
    (payment?.kind !== 'aga' || entry?.kind === 'aga') &&
    String(entry?.year || String(entry?.date || '').slice(0, 4) || '') === year &&
    payrollTaxPaymentTerm(entry) === term
  );
  const index = Math.max(0, sameTerm.findIndex((entry) => String(entry?.id) === String(payment.id)));
  return `AGA-${year}-T${term || 'X'}-${index + 1}`;
};
const payrollNetPayLiabilityRows = (run) => {
  const postedRows = safeArray(run?.payrollPostingRows)
    .filter((row) => !/OTP/i.test(String(row.description || '')))
    .map((row) => {
      const description = String(row.description || '').trim();
      const employeeMatch = description.match(/^Skyldig lønn\s*-\s*(.+)$/i);
      return {
        accountCode: String(row.accountCode || '').trim(),
        employeeName: employeeMatch ? employeeMatch[1].trim() : '',
        isNetPayLiability: Boolean(employeeMatch) || /^Skyldig lønn$/i.test(description),
        amount: num(row.credit) - num(row.debit),
      };
    })
    .filter((row) => row.amount > 0.005 && row.accountCode && (row.isNetPayLiability || /^2911(?::|$)/.test(row.accountCode)));
  if (postedRows.length) return postedRows;
  const rows = safeArray(run?.lines).flatMap((line) => [
    { accountCode: line.netPayLiabilityAccountCode || payrollEmployeeLiabilityAccountCode(line), employeeName: line.employeeName || 'Ansatt', amount: Math.max(0, num(line.netPay) - num(line.expenseReimbursement)) },
    { accountCode: line.expenseReimbursementLiabilityAccountCode || String(line.netPayLiabilityAccountCode || payrollEmployeeLiabilityAccountCode(line)).replace(/^2930(?=:|$)/, '2911'), employeeName: line.employeeName || 'Ansatt', amount: num(line.expenseReimbursement) },
  ])
    .filter((line) => line.accountCode && line.amount > 0.005);
  if (rows.length) return rows;
  const total = safeArray(run?.lines).reduce((sum, line) => sum + num(line.netPay), 0);
  return total > 0.005 ? [{ accountCode: '2930', employeeName: '', amount: total }] : [];
};
const payrollRunPostings = (run, state) => {
  if (!run || !['approved', 'paid'].includes(run.status)) return [];
  const rows = safeArray(run.payrollPostingRows).length
    ? safeArray(run.payrollPostingRows)
    : buildPayrollPostingRows(run, { ...(state?.payrollSettings || {}), preserveLegacyHolidayPayAccount: true });
  return rows.map((line) => posting(
    line.accountCode,
    line.description || plumme1AccountName(line.accountCode, `Konto ${line.accountCode}`),
    line.description || `L\u00f8nn ${run.period || ''}`,
    num(line.debit) - num(line.credit),
    {
      employeeId: line.employeeId,
      employeeName: line.employeeName,
      payrollPeriod: line.payrollPeriod || run.period,
      accrualYear: line.accrualYear,
    },
  ));
};
const payrollRunPaymentEvents = (run, state) => {
  if (!run || run.status === 'reversed') return [];
  const events = [];
  const employeePayments = safeArray(run.paymentStatus?.netPayPayments).filter((payment) => num(payment.amount) > 0.005 && payment.paidAt);
  employeePayments.forEach((payment, index) => events.push({
    id: `${run.id}-net-pay-${payment.id || index + 1}`, voucherNumber: `${payrollRunVoucherNumber(run, safeArray(state?.payrollRuns))}-BET-${index + 1}`, date: payment.paidAt, description: `Utbetaling lønn ${payment.employeeName || run.period || ''}`,
    postings: [...(safeArray(payment.liabilityAllocations).length ? safeArray(payment.liabilityAllocations).map((allocation) => posting(allocation.accountCode, allocation.kind === 'employee_expense' ? `Skyldige utlegg - ${payment.employeeName || 'Ansatt'}` : `Skyldig lønn - ${payment.employeeName || 'Ansatt'}`, `Utbetaling lønn og utlegg ${run.period || ''}`, num(allocation.amount), { employeeId: payment.employeeId, employeeName: payment.employeeName })) : [posting(payment.liabilityAccountCode || `2930:${payment.employeeId}`, payment.employeeName ? `Skyldig lønn - ${payment.employeeName}` : 'Skyldig lønn', `Utbetaling lønn ${run.period || ''}`, num(payment.amount))]),
      posting(payment.settlementAccountCode || '1920', 'Bank', `Utbetaling lønn ${run.period || ''}`, -num(payment.amount))],
  }));
  const netPaidAt = run.paymentStatus?.netPayPaidAt || run.paidAt;
  const netPayRows = payrollNetPayLiabilityRows(run);
  const netPay = netPayRows.reduce((sum, line) => sum + num(line.amount), 0);
  if (!employeePayments.length && netPaidAt && netPay > 0.005) events.push({
    id: `${run.id}-net-pay`, voucherNumber: `${payrollRunVoucherNumber(run, safeArray(state?.payrollRuns))}-BET`, date: netPaidAt, description: `Utbetaling lønn ${run.period || ''}`,
    postings: [
      ...netPayRows.map((line) => posting(line.accountCode, line.employeeName ? `Skyldig lønn - ${line.employeeName}` : 'Skyldig lønn', `Utbetaling lønn ${run.period || ''}`, line.amount)),
      posting(run.paymentStatus?.netPaySettlementAccountCode || run.netPaySettlementAccountCode || '1920', 'Bank', `Utbetaling lønn ${run.period || ''}`, -netPay),
    ],
  });
  const pensionPaidAt = run.paymentStatus?.pensionPaidAt; const pensionSettings = { ...(state?.payrollSettings || {}), country: state?.settings?.country, effectiveDate: run.paymentDate || run.paidAt };
  const pensionPaymentAmount = num(run.paymentStatus?.pensionPaidAmount) || safeArray(run.lines).reduce((sum, line) => sum + payrollLinePensionContribution(line, pensionSettings), 0); const pensionAmount = run.paymentStatus?.pensionPremiumAmount != null ? num(run.paymentStatus.pensionPremiumAmount) : pensionPaymentAmount;
  const pensionFeeAmount = Math.max(0, num(run.paymentStatus?.pensionFeeAmount)); const pensionFeeVatRate = Math.max(0, num(run.paymentStatus?.pensionFeeVatRate)); const pensionFeeVat = pensionFeeVatRate > 0 ? Math.round((pensionFeeAmount * pensionFeeVatRate / (100 + pensionFeeVatRate)) * 100) / 100 : 0; const pensionOverpayment = Math.max(0, num(run.paymentStatus?.pensionOverpaymentAmount));
  const frozenPensionLiabilityRows = safeArray(run.payrollPostingRows).filter((row) => ['2960', '2930'].includes(String(row.accountCode || '')) && /OTP/i.test(String(row.description || ''))); const pensionLiabilityAccountCode = String(run.paymentStatus?.pensionLiabilityAccountCodeAtPayment || frozenPensionLiabilityRows.find((row) => String(row.accountCode || '') === '2960')?.accountCode || frozenPensionLiabilityRows[0]?.accountCode || '2960'); const frozenPensionAccrual = frozenPensionLiabilityRows.filter((row) => String(row.accountCode || '') === pensionLiabilityAccountCode).reduce((sum, row) => sum + num(row.credit) - num(row.debit), 0);
  const pensionAccruedAmount = run.paymentStatus?.pensionAccruedAmountAtPayment != null ? num(run.paymentStatus.pensionAccruedAmountAtPayment) : (safeArray(run.payrollPostingRows).length ? frozenPensionAccrual : pensionAmount); const pensionAdjustment = pensionAmount - pensionAccruedAmount; const pensionEmployerContribution = Math.max(0, num(run.paymentStatus?.pensionEmployerContributionAmount));
  if (pensionPaidAt && pensionPaymentAmount > 0.005) events.push({ id: `${run.id}-pension-pay`, voucherNumber: `${payrollRunVoucherNumber(run, safeArray(state?.payrollRuns))}-OTP`, date: pensionPaidAt, description: `Betaling OTP ${run.period || ''}`,
    postings: [
      pensionAccruedAmount > 0.005 && posting(pensionLiabilityAccountCode, 'Skyldig OTP', `Betaling OTP ${run.period || ''}`, pensionAccruedAmount),
      Math.abs(pensionAdjustment) > 0.005 && posting('5950', 'Obligatorisk tjenestepensjon', `Avvik faktisk OTP-premie ${run.period || ''}`, pensionAdjustment),
      pensionFeeAmount - pensionFeeVat > 0.005 && posting(run.paymentStatus?.pensionFeeAccountCode || '7770', 'Pensjonsgebyr', `Gebyr OTP ${run.period || ''}`, pensionFeeAmount - pensionFeeVat), pensionFeeVat > 0.005 && posting('2710', 'Inngående merverdiavgift', `MVA på gebyr OTP ${run.period || ''}`, pensionFeeVat),
      pensionOverpayment > 0.005 && posting(run.paymentStatus?.pensionOverpaymentAccountCode || '1570', 'Forskudd pensjonsleverandør', `Overbetaling OTP ${run.period || ''}`, pensionOverpayment), posting(run.paymentStatus?.pensionSettlementAccountCode || '1920', 'Bank', `Betaling OTP ${run.period || ''}`, -pensionPaymentAmount),
      pensionEmployerContribution > 0.005 && posting('5400', 'Arbeidsgiveravgift', `AGA av betalt OTP-premie ${run.period || ''}`, pensionEmployerContribution), pensionEmployerContribution > 0.005 && posting('2770', 'Skyldig arbeidsgiveravgift', `AGA av betalt OTP-premie ${run.period || ''}`, -pensionEmployerContribution),
    ].filter(Boolean),
  });
  return events;
};
const payrollAccountingHistoryVouchers = (run, state) => safeArray(run?.accountingReversalHistory).flatMap((snapshot, snapshotIndex) => {
  // A draft never had an accounting event. Legacy snapshots without frozen
  // posting rows must not be reconstructed from editable pay lines.
  if (!safeArray(snapshot?.payrollPostingRows).length) return [];
  const originalRun = {
    id: `${run.id}-${snapshot.id}-original`,
    status: snapshot.paidAt || snapshot.paymentStatus ? 'paid' : 'approved',
    period: snapshot.period || run.period,
    paymentDate: snapshot.paymentDate || run.paymentDate,
    voucherNumber: snapshot.originalVoucherNumber || run.voucherNumber,
    lines: safeArray(snapshot.lines),
    payrollPostingRows: safeArray(snapshot.payrollPostingRows),
    paymentStatus: snapshot.paymentStatus,
    paidAt: snapshot.paidAt,
    netPaySettlementAccountCode: snapshot.netPaySettlementAccountCode,
  };
  const originalPayrollPostings = payrollRunPostings(originalRun, state);
  const originalPayments = payrollRunPaymentEvents(originalRun, { ...state, payrollRuns: [originalRun] });
  const originals = [
    originalPayrollPostings.length && {
      id: `${snapshot.id}-original`, sourceType: 'payrollHistory', sourceId: run.id,
      kind: 'lønn', num: originalRun.voucherNumber, name: `Lønn ${originalRun.period || ''}`.trim(),
      date: isoToNb(snapshot.originalPostingDate || payrollRunPostingDate(originalRun)), status: 'reversed',
      postings: originalPayrollPostings,
    },
    ...originalPayments.map((event) => ({
      id: `${snapshot.id}-${event.id}`, sourceType: 'payrollPaymentHistory', sourceId: run.id,
      kind: 'lønnsbetaling', num: event.voucherNumber, name: event.description,
      date: isoToNb(event.date), status: 'reversed', postings: event.postings,
    })),
  ].filter(Boolean);
  const reversalPostings = originals.flatMap((voucher) => safeArray(voucher.postings).map((line) => ({ ...line, amount: -num(line.amount) })));
  const reversal = reversalPostings.length ? [{
    id: snapshot.id, sourceType: 'payrollReversal', sourceId: run.id, kind: 'reversering-lønn',
    num: `${originalRun.voucherNumber || 'LØNN'}-REV-${snapshotIndex + 1}`,
    name: `Reversering lønn ${originalRun.period || ''}`.trim(), date: isoToNb(globalThis.PlummeAccountingScope.accountingEventDate(state, snapshot.reversedAt)),
    status: 'active', postings: reversalPostings,
  }] : [];
  return [...originals, ...reversal];
});
// Speiler `bankPostingVoucherNumber` i functions/bank/bankReconciliationMutationHttp.ts.
const bankPostingVoucherNumber = (id) => {
  const key = typeof id === 'string' ? id : '';
  if (!key) return 'BP-000000';
  let hash = 0x811c9dc5;
  for (let index = 0; index < key.length; index += 1) {
    hash ^= key.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `BP-${hash.toString(36).toUpperCase().padStart(6, '0').slice(-6)}`;
};

const bankReconciliationPostingLines = (entry) => {
  if (!entry || entry.status === 'annulled') return [];
  const explicitLines = safeArray(entry.lines).map((line) => posting(line.accountCode, plumme1AccountName(line.accountCode, `Konto ${line.accountCode}`), line.description || entry.description || 'Bankpostering', num(line.signedAmount))).filter((line) => line.nr && num(line.amount));
  if (explicitLines.length) return explicitLines;
  if (!num(entry.amount)) return [];
  const amount = Math.abs(num(entry.amount));
  const description = entry.description || 'Bankpostering';
  return [
    posting(entry.debitAccountCode, plumme1AccountName(entry.debitAccountCode, `Konto ${entry.debitAccountCode}`), description, amount),
    posting(entry.creditAccountCode, plumme1AccountName(entry.creditAccountCode, `Konto ${entry.creditAccountCode}`), description, -amount),
  ].filter((line) => line.nr);
};
const addOpeningBalanceToTrial = (map, state, year) => {
  const opening = state.openingBalanceByYear?.[String(year)];
  if (!opening) return;
  const openingLines=safeArray(opening.completedEntry?.lines).length?safeArray(opening.completedEntry.lines):safeArray(opening.balanceLines);
  openingLines.forEach((line) => {
    addTrialAmount(map, state, line.accountCode || line.code, num(line.debit) - num(line.credit));
  });
  const explicitCodes = new Set(openingLines.map((line) => String(line.accountCode || line.code || '').trim()));
  const addStartup = (code, amount) => { if (!explicitCodes.has(String(code).trim())) addTrialAmount(map, state, code, amount); };
  safeArray(opening.salesByAccount).forEach((line) => addStartup(line.accountCode || '3000', -Math.abs(num(line.amount))));
  safeArray(opening.expensesByAccount).forEach((line) => addStartup(line.accountCode || '6300', Math.abs(num(line.amount))));
  if (String(state.settings?.country || '').toUpperCase() === 'NO') {
    addStartup('2700', -Math.abs(num(opening.outgoingVat)));
    addStartup('2710', Math.abs(num(opening.incomingVat)));
  }
};
// En import kan legge foretakets egen konto pa en variant av standardkoden — Fiken
// eksporterer driftskontoen som `1920:10001`. Da er det varianten som foerer, og den
// bare 4-sifrede standardkoden finnes ikke lenger i regnskapet. Kartet fra standardkode
// til variant deles med Saldo-kortet, sa begge steder skjuler og slaar opp likt.
const chartShadowedCodeMap = (state) => {
  const map = new Map();
  mergePlumme1ChartAccounts(state.chartAccounts, state.settings)
    .map((account) => String(account.code || account.accountNumber || '').trim())
    .filter((code) => /^\d{4}[:._-]/.test(code))
    .forEach((code) => { if (!map.has(code.slice(0, 4))) map.set(code.slice(0, 4), code); });
  return map;
};
const createTrialTotalsMap = (state) => {
  const totals = new Map();
  const chartAccounts = mergePlumme1ChartAccounts(state.chartAccounts, state.settings);
  const shadowedStandardCodes = new Set(chartShadowedCodeMap(state).keys());
  chartAccounts
    .filter((account) => account.active !== false)
    .filter((account) => !shadowedStandardCodes.has(String(account.code || account.accountNumber || '').trim()))
    .forEach((account) => totals.set(String(account.code || account.accountNumber), {
      nr: String(account.code || account.accountNumber),
      name: trialAccountDisplayName(state, account.code || account.accountNumber, account.name),
      category: account.category,
      ut: 0,
    }));
  return totals;
};
const previousIsoDay = (iso) => {
  const date = new Date(`${nbToIso(iso)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() - 1);
  return date.toISOString().slice(0, 10);
};
const monthStartIso = (year, monthIndex) => `${year}-${String(monthIndex + 1).padStart(2, '0')}-01`;
const monthEndIso = (year, monthIndex) => `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(new Date(year, monthIndex + 1, 0).getDate()).padStart(2, '0')}`;
const buildTrialTotalsForRange = (state, year, range, { includeOpeningBalance = false, carryForward = null } = {}) => {
  const totals = createTrialTotalsMap(state);
  const rangeOptions = Array.isArray(range) ? { months: range } : { ...(range || {}) };
  if (rangeOptions.fiscalYear) rangeOptions.fiscalBounds = window.PlummeTrialBalanceContinuity.fiscalBounds(state.settings, year);
  const ledger = ledgerBalancesFor(state, year, rangeOptions); // Read model instead of the journal replay when it can answer (backend-ledger-model.jsx).
  const journalOpenings = ledger ? ledgerOpeningVouchers(ledger, year, rangeOptions) : Array.isArray(state.accountingEvents) ? accountingJournalVouchers(state).filter((voucher) => voucher.sourceType === 'openingBalance' && (rangeOptions.fiscalBounds ? nbToIso(voucher.date) >= rangeOptions.fiscalBounds.from && nbToIso(voucher.date) <= rangeOptions.fiscalBounds.to : nbToIso(voucher.date).startsWith(`${year}-`))) : [];
  const months = Array.isArray(rangeOptions.months) ? rangeOptions.months : [0, 11];
  const dimensionActive = reportDimensionApi().active(rangeOptions);
  const includeUnassignedBase = !dimensionActive || reportDimensionApi().includeUnassignedBase(rangeOptions);
  if (includeOpeningBalance && includeUnassignedBase) {
    if (usesImportedReportingBasis(state, year)) addImportedOpeningBalanceToTrial(totals, state, year);
    else if (journalOpenings.length) journalOpenings.forEach((voucher) => safeArray(voucher.postings).forEach((line) => addPostingToTrial(totals, state, line)));
    else if (!Array.isArray(state.accountingEvents) && importedReportStatsForYear(state, year)) addImportedOpeningBalanceToTrial(totals, state, year);
    else if (!Array.isArray(state.accountingEvents) && Object.prototype.hasOwnProperty.call(state.openingBalanceByYear || {}, String(year))) addOpeningBalanceToTrial(totals, state, year);
    else if ((Array.isArray(state.accountingEvents) || !window.PlummeTrialBalanceContinuity.hasExplicitOpening(state, year)) && carryForward) window.PlummeTrialBalanceContinuity.addPreviousClosingBalance({ state, year, context: carryForward, buildClosingTotals: (previousYear) => buildTrialTotalsForRange(state, previousYear, { months: [0, 17], fiscalYear: rangeOptions.fiscalYear }, { includeOpeningBalance: true, carryForward }), addAccount: (account) => addTrialAmount(totals, state, account.nr, account.ut, account.name) });
  }
  if (includeUnassignedBase) addImportedReportMovementsToTrial(totals, state, year, rangeOptions);
  if (ledger) { addLedgerMovementsToTrial(totals, state, ledger, year, rangeOptions); return totals; }
  deriveAccountingLedgerVouchers(state).forEach((voucher) => {
    if (voucher.sourceType === 'openingBalance' || !reportDateInRange(nbToIso(voucher.date), year, rangeOptions)) return;
    reportDimensionApi().postings(state, voucher, rangeOptions).forEach((line) => addPostingToTrial(totals, state, line));
  });
  return totals;
};
const deriveTrialBalance = (state, year, options = {}) => {
  const groups = window.PlummeTrialBalanceDispositions(TRIAL_GROUPS, window.PlummeAccountingScope.settingsCountryCode(state.settings));
  if (options.fiscalYear) return window.PlummeTrialBalanceContinuity.fiscalTrial({ state, year, options, buildTotals: buildTrialTotalsForRange, groups });
  const months = Array.isArray(options.months) ? options.months : [0, 11];
  const monthStart = Math.max(0, Math.min(11, num(months[0], 0)));
  const monthEnd = Math.max(monthStart, Math.min(11, num(months[1], 11)));
  const selectedFrom = options.from || monthStartIso(year, monthStart);
  const selectedTo = options.to || monthEndIso(year, monthEnd);
  const yearStart = monthStartIso(year, 0); const openingTo = previousIsoDay(selectedFrom);
  const carryForward = window.PlummeTrialBalanceContinuity.createContext(state, year);
  const openingTotals = openingTo && openingTo >= yearStart
    ? buildTrialTotalsForRange(state, year, { from: yearStart, to: openingTo, months: [0, 11], projectId: options.projectId, departmentId: options.departmentId }, { includeOpeningBalance: true, carryForward })
    : buildTrialTotalsForRange(state, year, { months: [0, -1], projectId: options.projectId, departmentId: options.departmentId }, { includeOpeningBalance: true, carryForward });
  const movementTotals = buildTrialTotalsForRange(state, year, { from: selectedFrom, to: selectedTo, months: [monthStart, monthEnd], projectId: options.projectId, departmentId: options.departmentId }, { includeOpeningBalance: false });
  const allCodes = new Set([...openingTotals.keys(), ...movementTotals.keys()]);
  const accounts = [...allCodes].map((code) => {
    const opening = openingTotals.get(code); const movement = movementTotals.get(code) || { ut: 0 };
    const metadata = opening || movement || { nr: code, name: trialAccountName(state, code), category: trialAccountCategory(state, code), ut: 0 };
    const inn = Math.round(num(opening?.ut) * 100) / 100;
    const endring = Math.round(num(movement.ut) * 100) / 100;
    return {
      ...metadata,
      nr: String(metadata.nr || code),
      name: metadata.name || trialAccountName(state, code),
      category: metadata.category || trialAccountCategory(state, code),
      inn,
      endring,
      ut: Math.round((inn + endring) * 100) / 100,
    };
  })
    .sort((a, b) => a.nr.localeCompare(b.nr, 'nb-NO'));
  return groups.map((group) => ({
    key: group.key,
    labelKey: group.labelKey,
    accounts: accounts.filter((account) => group.match(account)),
  }));
};

// The engine returns translation keys, not display text. UI language and accounting
// country are independent, so the page translates them with t() in the viewer's locale.
const REPORT_RESULT_LABEL_KEYS = Object.freeze({
  title: 'nav.incomeStatement',
  subtitle: 'rep.result.subtitle',
  income: 'rep.result.income',
  costs: 'rep.result.costs',
  finance: 'rep.result.finance',
  result: 'rep.result.beforeTax',
  noLines: 'rep.result.noLines',
  total: 'rep.total',
  showDetails: 'rep.showDetails',
  hideDetails: 'rep.hideDetails',
});
const reportIsFullYear = (options = {}) =>
  !options.from &&
  !options.to &&
  (!Array.isArray(options.months) || (Number(options.months[0]) <= 0 && Number(options.months[1]) >= 11));
const addOpeningBalanceToReport = (state, year, incomeByAccount, costByAccount, options = {}) => {
  if (!reportIsFullYear(options)) return;
  if (reportDimensionApi().active(options) && !reportDimensionApi().includeUnassignedBase(options)) return;
  const opening = state.openingBalanceByYear?.[String(year)];
  if (!opening) return;
  const balances = safeArray(opening.completedEntry?.lines).length?safeArray(opening.completedEntry.lines):safeArray(opening.balanceLines);
  const explicitCodes = new Set(balances.map((line) => String(line.accountCode || line.code || '').trim()));
  const startupRows = [
    ...safeArray(opening.salesByAccount).map((line) => ({ ...line, accountCode: line.accountCode || '3000', debit: 0, credit: Math.abs(num(line.amount)) })),
    ...safeArray(opening.expensesByAccount).map((line) => ({ ...line, accountCode: line.accountCode || '6300', debit: Math.abs(num(line.amount)), credit: 0 })),
  ].filter((line) => !explicitCodes.has(String(line.accountCode).trim()));
  [...balances, ...startupRows].forEach((line) => {
    const code = line.accountCode || line.code;
    const amount = num(line.debit) - num(line.credit);
    if (isReportIncomeAccountCode(state, code)) pushReportAmount(incomeByAccount, state, code, line.name || line.description || 'Oppstart inntekt', -amount);
    if (isReportCostAccountCode(state, code)) pushReportAmount(costByAccount, state, code, line.name || line.description || 'Oppstart kostnad', amount);
  });
};
const addImportedReportStatsToReport = (state, year, incomeByAccount, costByAccount, options = {}) => {
  if (reportDimensionApi().active(options) && !reportDimensionApi().includeUnassignedBase(options)) return;
  const accountNames = new Map(safeArray(importedReportStatsForYear(state, year)?.accounts)
    .map((account) => [String(account.accountCode || ''), account.name]));
  const movementByAccount = new Map();
  safeArray(importedReportStatsForYear(state, year)?.monthly).forEach((row) => {
    if (!importedReportMonthInRange(row.month, year, options)) return;
    const code = String(row.accountCode || '').trim();
    if (!code) return;
    if (isImportedClosingTransferAccountCode(code)) return;
    movementByAccount.set(code, num(movementByAccount.get(code)) + num(row.movement));
  });
  movementByAccount.forEach((movement, code) => {
    const name = accountNames.get(code) || (isFinanceAccountCode(code) ? 'Importert finanspost' : 'Importert resultatpost');
    if (isFinanceAccountCode(code)) {
      const target = movement < 0 ? incomeByAccount : costByAccount;
      pushReportAmount(target, state, code, name, Math.abs(movement));
    } else if (isReportIncomeAccountCode(state, code)) {
      pushReportAmount(incomeByAccount, state, code, name, -movement);
    } else if (isReportCostAccountCode(state, code)) {
      pushReportAmount(costByAccount, state, code, name, movement);
    }
  });
};
const splitReportMaps = (state, incomeByAccount, costByAccount) => {
  const operatingIncome = new Map();
  const financialIncome = new Map();
  const operatingCosts = new Map();
  const financialCosts = new Map();
  incomeByAccount.forEach((line, code) => { if (window.PlummeAccountingScope.settingsCountryCode(state.settings) === 'NO' && /^8[389]\d{2}(?::|$)/.test(String(code))) return;
    (isFinanceAccountCode(code) ? financialIncome : operatingIncome).set(code, line);
  });
  costByAccount.forEach((line, code) => { if (window.PlummeAccountingScope.settingsCountryCode(state.settings) === 'NO' && /^8[389]\d{2}(?::|$)/.test(String(code))) return;
    (isFinanceAccountCode(code) ? financialCosts : operatingCosts).set(code, line);
  });
  return { operatingIncome, financialIncome, operatingCosts, financialCosts };
};
// Akkumuleringen av bokforte poster deles av arsrapporten og manedsgrafen. `mapsFor`
// velger hvilke kontokart en post havner i ut fra maneden sin: arsrapporten gir det
// samme paret uansett maned, grafen gir ett par per maned. Da kan grafen bygges i ett
// gjennomlop over bilagene i stedet for a kjore hele rapporten en gang per maned, og de
// to kan ikke komme i utakt fordi de kjorer den samme koden.
const addPostingLinesToReport = (state, lines, maps) => {
  safeArray(lines).forEach((line) => {
    const code = String(line.nr || line.accountCode || '').trim();
    const amount = num(line.amount ?? line.signedAmount);
    const label = line.name || line.description || `Konto ${code}`;
    if (isClosingOrTaxDispositionAccountCode(code)) return;
    if (isFinanceAccountCode(code)) {
      pushReportAmount(amount < 0 ? maps.incomeByAccount : maps.costByAccount, state, code, label, Math.abs(amount));
      return;
    }
    if (isReportCostAccountCode(state, code)) pushReportAmount(maps.costByAccount, state, code, label, amount);
    if (isReportIncomeAccountCode(state, code)) pushReportAmount(maps.incomeByAccount, state, code, label, -amount);
  });
};
const accumulateReportEntries = (state, year, options, mapsFor) => {
  const ledger = ledgerBalancesFor(state, year, options);
  if (ledger) { addLedgerPostingsToReport(state, ledger, year, options, mapsFor); return; } // Every posting of the month, openings included, like the fold below.
  deriveAccountingLedgerVouchers(state).forEach((voucher) => {
    const monthIndex = reportMonthIndex(nbToIso(voucher.date), year, options);
    if (monthIndex < 0) return;
    addPostingLinesToReport(state, reportDimensionApi().postings(state, voucher, options), mapsFor(monthIndex));
  });
};

// Summene rapporten og grafen deler. Skillet mellom drift og finans ma gjores for det
// summeres, fordi finansposter ikke inngar i driftsresultatet.
const reportTotalsFromMaps = (state, incomeByAccount, costByAccount) => {
  const { operatingIncome, financialIncome, operatingCosts, financialCosts } = splitReportMaps(state, incomeByAccount, costByAccount);
  return {
    operatingIncome,
    financialIncome,
    operatingCosts,
    financialCosts,
    income: [...operatingIncome.values()].reduce((sum, line) => sum + line.amount, 0),
    cost: [...operatingCosts.values()].reduce((sum, line) => sum + line.amount, 0),
    financialIncomeTotal: [...financialIncome.values()].reduce((sum, line) => sum + line.amount, 0),
    financialCostTotal: [...financialCosts.values()].reduce((sum, line) => sum + line.amount, 0),
  };
};

const deriveReportResult = (state, year, options = {}) => {
  const incomeByAccount = new Map();
  const costByAccount = new Map();
  if (importedReportStatsForYear(state, year)) addImportedReportStatsToReport(state, year, incomeByAccount, costByAccount, options);
  else addOpeningBalanceToReport(state, year, incomeByAccount, costByAccount, options);
  const maps = { incomeByAccount, costByAccount };
  accumulateReportEntries(state, year, options, () => maps);
  const {
    operatingIncome, financialIncome, operatingCosts, financialCosts,
    income, cost, financialIncomeTotal, financialCostTotal,
  } = reportTotalsFromMaps(state, incomeByAccount, costByAccount);
  const finance = financialIncomeTotal - financialCostTotal;
  const labelKeys = REPORT_RESULT_LABEL_KEYS;
  return {
    labelKeys,
    inntekter: income,
    kostnader: cost,
    finansinntekter: financialIncomeTotal,
    finanskostnader: financialCostTotal,
    totaleInntekter: income + financialIncomeTotal,
    totaleKostnader: cost + financialCostTotal,
    finans: finance,
    resultat: income - cost + finance,
    sections: [
      { key: 'inntekter', labelKey: labelKeys.income, tone: 'green', total: income, lines: reportLinesFromMap(operatingIncome, income) },
      { key: 'kostnader', labelKey: labelKeys.costs, tone: 'peach', total: cost, lines: reportLinesFromMap(operatingCosts, cost) },
      {
        key: 'finans',
        labelKey: labelKeys.finance,
        tone: 'plum',
        total: finance,
        lines: reportLinesFromMap(financialIncome, Math.abs(finance), financialCosts),
      },
    ],
  };
};

// Grafen bygges i ett gjennomlop over bilagene, med en botte per maned. Den kalte for
// deriveReportResult en gang per maned, som ga tolv fulle gjennomganger av alle bilag
// for a produsere tolv sma tall.
//
// Inngaende balanse er utelatt med vilje: den har ingen dato og horer ikke hjemme i en
// enkelt maned. Det var ogsa adferden for, siden addOpeningBalanceToReport bare kjorer
// for hele ar og en enkeltmaned aldri er det.
const deriveReportChart = (state, year, options = {}) => {
  const bounds = options.fiscalBounds;
  const count = bounds ? (Number(bounds.to.slice(0, 4)) - Number(bounds.from.slice(0, 4))) * 12 + Number(bounds.to.slice(5, 7)) - Number(bounds.from.slice(5, 7)) + 1 : 12;
  const monthly = Array.from({ length: count }, () => ({ incomeByAccount: new Map(), costByAccount: new Map() }));
  if (importedReportStatsForYear(state, year)) {
    // Importerte tall summeres per maned for fortegnet avgjores, akkurat som nar
    // rapporten ble kjort med et enkelt manedsvindu.
    monthly.forEach((maps, monthIndex) =>
      addImportedReportStatsToReport(state, year, maps.incomeByAccount, maps.costByAccount, { months: [monthIndex, monthIndex], projectId: options.projectId, departmentId: options.departmentId }));
  }
  accumulateReportEntries(state, year, { fiscalBounds: bounds, projectId: options.projectId, departmentId: options.departmentId }, (monthIndex) => monthly[monthIndex]);
  return monthly.map(({ incomeByAccount, costByAccount }) => {
    const { income, cost, financialIncomeTotal, financialCostTotal } = reportTotalsFromMaps(state, incomeByAccount, costByAccount);
    return {
      inc: Math.round(options.includeFinance ? income + financialIncomeTotal : income),
      cost: Math.round(options.includeFinance ? cost + financialCostTotal : cost),
    };
  });
};

// Callers may omit chart/result/trial independently; the default builds all three.
const deriveReports = (state, year, options = {}) => {
  // One chart per synchronous report; never reuse it across mutable state or locale changes.
  state = { ...state };
  reportTrialAccountContexts.set(state, mergePlumme1ChartAccounts(state.chartAccounts, state.settings));
  if (options.fiscalYear) options = { ...options, fiscalBounds: window.PlummeTrialBalanceContinuity.fiscalBounds(state.settings, year) };
  return {
  chart: options.includeChart === false ? null : deriveReportChart(state, year, options),
  result: options.includeResult === false ? null : deriveReportResult(state, year, options),
  trial: options.includeTrial === false ? null : deriveTrialBalance(state, year, options),
  dimensions: reportDimensionApi().options(state),
  };
};
